import React from 'react';
import { Calendar, ShieldCheck, CheckCircle, HelpCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BOOKING_LINK = "https://system.easypractice.net/book/akupunktur-klinikken-acu-sense";

export default function Prices() {
  const priceItems = [
    {
      title: "Første behandling",
      description: "Inkluderer grundig journaloptagelse, samtale, undersøgelse og den første akupunkturbehandling. Medbring venligst et lagen.",
      duration: "ca. 60 minutter",
      price: "450,00 kr."
    },
    {
      title: "Efterfølgende behandlinger",
      description: "Opfølgende samtale og målrettet akupunkturbehandling tilpasset dit igangværende forløb. Medbring venligst et lagen.",
      duration: "30 - 60 minutter",
      price: "550,00 kr."
    },
    {
      title: "Klippekort (5 behandlinger)",
      description: "Et fordelagtigt klippekort til efterfølgende behandlinger. Perfekt til længerevarende behandlingsforløb. Kan ikke refunderes.",
      duration: "5 x 30-60 minutter",
      price: "2.500,00 kr."
    },
    {
      title: "Myggebehandling",
      description: "Hurtig og forebyggende akupunkturbehandling mod myggestik. Særdeles effektiv og smertefri. Medbring venligst et lagen.",
      duration: "15 minutter",
      price: "150,00 kr."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Prisliste | Klinik Acu-sense</title>
        <meta name="description" content="Se priser for akupunkturbehandlinger hos Klinik Acu-sense in Hammel. Få tilskud fra Sygeforsikringen Danmark og private sundhedsforsikringer." />
        <meta property="og:title" content="Prisliste | Klinik Acu-sense" />
        <meta property="og:description" content="Se priser for akupunkturbehandlinger hos Klinik Acu-sense in Hammel. Få tilskud fra Sygeforsikringen Danmark og private sundhedsforsikringer." />
        <link rel="canonical" href="https://www.acu-sense.dk/priser" />
      </Helmet>

      {/* Page Header */}
      <section className="bg-[#F3F1EC] py-16 border-b border-[#E6E4DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C3531] tracking-tight">
            Priser & Tilskud
          </h1>
          <p className="text-base sm:text-lg text-[#5C6360] max-w-2xl mx-auto leading-relaxed">
            Her finder du en overskuelig oversigt over mine behandlingspriser. Jeg er RAB-godkendt, hvilket giver dig gode muligheder for økonomisk tilskud.
          </p>
        </div>
      </section>

      {/* Price List Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FAF9F6] rounded-3xl border border-[#E6E4DF] overflow-hidden shadow-sm">
            <div className="p-6 sm:p-10 space-y-8">
              <div className="flex items-center justify-between border-b border-[#E6E4DF] pb-6">
                <h2 className="font-serif text-2xl font-bold text-[#2C3531]">
                  Behandlingsoversigt
                </h2>
                <span className="text-xs text-[#8A908D] font-semibold uppercase tracking-wider">
                  Gældende pr. 1. januar 2026
                </span>
              </div>

              <div className="divide-y divide-[#E6E4DF]">
                {priceItems.map((item, idx) => (
                  <div key={idx} className="py-6 first:pt-0 last:pb-0 grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                    <div className="md:col-span-8 space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-serif text-lg font-bold text-[#2C3531]">
                          {item.title}
                        </h3>
                        <span className="text-xs text-[#5F7464] bg-[#8FA89B]/10 px-2 py-0.5 rounded-full font-medium">
                          {item.duration}
                        </span>
                      </div>
                      <p className="text-sm text-[#5C6360] leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                    <div className="md:col-span-4 text-left md:text-right">
                      <span className="font-serif text-xl font-bold text-[#2C3531] block">
                        {item.price}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Medbring lagen note */}
              <div className="pt-6 border-t border-[#E6E4DF] text-xs text-[#8A908D] flex items-start gap-2">
                <HelpCircle className="w-4 h-4 text-[#5F7464] shrink-0 mt-0.5" />
                <span>Husk venligst at medbringe et rent lagen eller stort håndklæde til alle behandlinger i klinikken.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subsidies & Insurances Detail */}
      <section className="py-20 bg-[#FAF9F6] border-t border-b border-[#E6E4DF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#5F7464]/10 text-[#3D4F44] text-xs font-bold tracking-wider uppercase">
              <ShieldCheck className="w-4 h-4" />
              Tilskudsmuligheder
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531] tracking-tight">
              RAB-godkendelse sikrer dig tilskud
            </h2>
            <p className="text-sm sm:text-base text-[#5C6360]">
              Brian Hangaard er Registreret Alternativ Behandler (RAB). Det er din garanti for høj faglig uddannelse og giver dig adgang til økonomisk dækning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Sygeforsikringen Danmark */}
            <div className="bg-white p-8 rounded-2xl border border-[#E6E4DF] space-y-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F3F1EC] flex items-center justify-center text-[#5F7464]">
                  <CheckCircle className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-lg font-bold text-[#2C3531]">
                  Sygeforsikringen Danmark
                </h3>
              </div>
              <p className="text-sm text-[#5C6360] leading-relaxed">
                Der ydes tilskud til akupunkturbehandlinger udført af en RAB-godkendt akupunktør. Jeg indberetter automatisk din behandling til Danmark, så du modtager dit tilskud direkte.
              </p>
              <p className="text-xs text-[#8A908D] italic">
                Jeg opfordrer dig til at kontakte Sygeforsikringen Danmark eller besøge deres hjemmeside for at undersøge dine præcise tilskudsmuligheder ud fra din valgte sygesikringsgruppe.
              </p>
            </div>

            {/* Private Sundhedsforsikringer */}
            <div className="bg-white p-8 rounded-2xl border border-[#E6E4DF] space-y-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F3F1EC] flex items-center justify-center text-[#5F7464]">
                  <CheckCircle className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-lg font-bold text-[#2C3531]">
                  Sundhedsforsikringer
                </h3>
              </div>
              <p className="text-sm text-[#5C6360] leading-relaxed">
                Langt de fleste private sundhedsforsikringer (f.eks. Danica, PFA, Topdanmark, Skandia m.fl.) dækker akupunkturbehandlinger helt eller delvist, når behandleren er RAB-godkendt.
              </p>
              <p className="text-xs text-[#8A908D] italic">
                Jeg anbefaler, at du kontakter dit forsikringsselskab inden den første behandling for at få en skriftlig bekræftelse på dækning af behandlingsforløbet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531]">
            Klar til at bestille din tid?
          </h2>
          <p className="text-sm sm:text-base text-[#5C6360] max-w-xl mx-auto">
            Min online booking er åben hele døgnet. Find nemt en tid, der passer ind i din kalender.
          </p>
          <div className="pt-4">
            <a
              href={BOOKING_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#5F7464] text-white font-medium tracking-wide hover:bg-[#4E6052] transition-colors duration-200 shadow-md"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Gå til booking
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
